import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { BASE_URL } from '../../insightConfig/insightConfig';
import './Live_streaming.css';
import EmojiPicker from 'emoji-picker-react';

const LiveClassChat = ({ setIsChatOpen, stream }) => {
    const socket = useRef(null);
    const [messages, setMessages] = useState([]);
    const [messageText, setMessageText] = useState('');
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const pickerRef = useRef(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    // Ref to track last message for deduplication (Safety net)
    const lastMessageRef = useRef(null);

    const emailToken = localStorage.getItem('authtoken');
    const [userDetails, setUserDetails] = useState({});

    // Fetch student details
    useEffect(() => {
        if (!emailToken) return;

        axios
            .get(`${BASE_URL}/studentdashboard`)
            .then(res => {
                setUserDetails(res.data.data[0] || {});
            })
            .catch(err => console.error(err));
    }, [emailToken]);

    // Socket connection
    useEffect(() => {
        if (!stream?.Sn || !userDetails?.EmailId) return;

        // --- FIX 2: Prevent Double Connection ---
        // Only initialize connection if socket.current is null
        if (!socket.current) {
            socket.current = io(BASE_URL, {
                transports: ['websocket'],
                withCredentials: true,
                auth: {
                    email: userDetails.EmailId,
                    name: userDetails.FirstName,
                    role: 'student'
                }
            });
        }

        const currentSocket = socket.current;

        // Ensure we don't attach multiple 'connect' listeners if effect re-runs
        if (!currentSocket.hasListeners('connect')) {
            currentSocket.on('connect', () => {
                currentSocket.emit('joinRoom', stream.Sn);
            });
        } else {
            // If already connected/listeners exist, just ensure we are in the room
            if (currentSocket.connected) {
                currentSocket.emit('joinRoom', stream.Sn);
            }
        }

        const handleChatHistory = msgs => setMessages(msgs);

        const handleChatMessage = msg => {
            const now = Date.now();
            // Deduplication logic: Check if identical to last message received within 500ms
            if (
                lastMessageRef.current &&
                lastMessageRef.current.Message === msg.Message &&
                lastMessageRef.current.SenderEmail === msg.SenderEmail &&
                (now - lastMessageRef.current.receivedAt) < 500
            ) {
                return;
            }

            // Update last message ref
            lastMessageRef.current = { ...msg, receivedAt: now };
            setMessages(prev => [...prev, msg]);
        };

        // Remove old listeners before adding new ones to avoid duplicates
        currentSocket.off('chatHistory');
        currentSocket.off('chatMessage');

        currentSocket.on('chatHistory', handleChatHistory);
        currentSocket.on('chatMessage', handleChatMessage);

        // Cleanup function
        return () => {
            currentSocket.off('chatHistory', handleChatHistory);
            currentSocket.off('chatMessage', handleChatMessage);
            
            // Optional: Disconnect on unmount if you want to close the socket entirely
            // currentSocket.disconnect();
            // socket.current = null; 
        };
    }, [stream?.Sn, userDetails?.EmailId, userDetails?.FirstName]);

    // Scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages.length]);

    // Emoji outside click
    useEffect(() => {
        const handleClickOutside = e => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setShowEmojiPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const renderMessage = (text, mentions) => {
        if (!mentions) return text;

        let mentionList = [];

        if (typeof mentions === 'string') {
            try {
                mentionList = JSON.parse(mentions);
            } catch {
                mentionList = [];
            }
        } else if (Array.isArray(mentions)) {
            mentionList = mentions;
        }

        if (!mentionList.length) return text;

        const parts = [];
        let remaining = text;

        mentionList.forEach((name, idx) => {
            const mention = `@${name}`;
            const index = remaining.indexOf(mention);

            if (index !== -1) {
                parts.push(remaining.slice(0, index));
                parts.push(
                    <span key={`${name}-${idx}`} className="mention">
                        {mention}
                    </span>
                );
                remaining = remaining.slice(index + mention.length);
            }
        });

        parts.push(remaining);
        return parts;
    };

    const sendMessage = e => {
        e.preventDefault();
        // Trim whitespace to prevent empty messages
        if (!messageText.trim() || loading) return;

        setLoading(true);

        // Use the existing socket reference
        if (socket.current) {
            socket.current.emit('chatMessage', {
                ClassId: stream.Sn,
                SenderEmail: userDetails.EmailId,
                SenderName: userDetails.FirstName,
                SenderRole: 'student',
                Message: messageText.trim(),
                CreatedAt: new Date(),
            });
        }

        setMessageText('');

        // Prevent double submit for 500ms
        setTimeout(() => setLoading(false), 500);
    };

    const formatTime = ts =>
        ts
            ? new Date(ts).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
            })
            : '';

    return (
        <div className="live-class-chat-container">
            <button className="close-btn" onClick={() => setIsChatOpen(false)}>
                –
            </button>

            <div className="chat-header">
                <p>Live Chats – {stream?.Title}</p>
            </div>

            <div className="chat-messages-area">
                {messages.map((msg, i) => {
                    const myMsg = msg.SenderEmail === userDetails.EmailId;
                    return (
                        <div
                            key={i}
                            className={`chat-message-wrapper ${myMsg ? 'own-message' : msg.SenderRole === 'admin'
                                ? 'teacher-message'
                                : 'student-message'
                                }`}
                        >
                            <div className="chat-message">
                                <div className="message-sender">
                                    <strong>
                                        {msg.SenderRole === 'admin'
                                            ? 'Insight Delhi'
                                            : msg.SenderName}
                                    </strong>
                                    <span className="message-time ms-1">
                                        {formatTime(msg.CreatedAt)}
                                    </span>
                                </div>
                                <div
                                    style={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}
                                    className="message-content">
                                    {msg.ReplyToMessageText && (
                                        <div
                                            style={{
                                                background: '#f1f5f9',
                                                padding: '6px',
                                                borderLeft: '3px solid #2563eb',
                                                marginBottom: '6px',
                                                fontSize: '12px',
                                                borderRadius: '4px'
                                            }}
                                        >
                                            <strong style={{ color: '#2563eb' }}>
                                                Replying to {msg.ReplyToSenderName}
                                            </strong>
                                            <div
                                                style={{
                                                    color: '#334155',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                {msg.ReplyToMessageText}
                                            </div>
                                        </div>
                                    )}
                                    {renderMessage(msg.Message, msg.Mentions)}
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            <form onSubmit={sendMessage} className="chat-input-area">
                {showEmojiPicker && (
                    <div ref={pickerRef} className="emoji-picker">
                        <EmojiPicker
                            onEmojiClick={e =>
                                setMessageText(prev => prev + e.emoji)
                            }
                        />
                    </div>
                )}

                <div className="input-group d-flex">
                    <div style={{ position: 'relative', width: '100%' }}>
                        <input
                            style={{ 
                                width: '100%', 
                                marginRight: '15px',
                                // --- FIX: This forces the input to show text exactly as typed ---
                                textTransform: 'none' 
                            }}
                            type="text"
                            className="form-control"
                            value={messageText}
                            onChange={e => setMessageText(e.target.value)}
                            placeholder="Type your message..."
                            maxLength={500}
                            
                            // Optional: Keeps the iOS keyboard from capitalizing the very first letter automatically
                            autoCapitalize="sentences" 
                            autoComplete="off"
                            autoCorrect="off"
                        />
                        <span
                            style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                            className="emoji-btn"
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        >
                            😀
                        </span>
                    </div>
                    <button className="btn btn-primary" type="submit" disabled={loading}>
                        {loading ? '...' : 'Send'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default React.memo(LiveClassChat);